import { Analytics } from '@vercel/analytics/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { WeightProvider } from './lib/context';

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then(() => {
        // Service worker registered successfully
      })
      .catch(() => {
        // Service worker registration failed silently
      });
  });
}

// Error boundary component for analytics
class AnalyticsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging but don't break the app
    if (typeof window !== 'undefined') {
      (window as any).analyticsError = { error: error.message, info: errorInfo };
    }
  }

  render() {
    if (this.state.hasError) {
      // Return null if analytics crashes, don't break the app
      return null;
    }

    return this.props.children;
  }
}

// Comprehensive analytics component with error handling
const AnalyticsWithErrorBoundary = () => {
  // Check if we're in a Vercel environment
  const isVercel = window.location.hostname.includes('vercel.app') || 
                   window.location.hostname.includes('coldprocesssoaps.com');

  try {
    if (isVercel) {
      // Add analytics status to window for debugging
      if (typeof window !== 'undefined') {
        (window as any).analyticsStatus = 'enabled';
      }
      return (
        <AnalyticsErrorBoundary>
          <Analytics />
        </AnalyticsErrorBoundary>
      );
    }
    // Don't load analytics in non-Vercel environments to prevent errors
    if (typeof window !== 'undefined') {
      (window as any).analyticsStatus = 'disabled';
    }
    return null;
  } catch (error) {
    // If analytics fails, don't break the app
    if (typeof window !== 'undefined') {
      (window as any).analyticsStatus = 'error';
    }
    return null;
  }
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <WeightProvider>
        <App />
        <AnalyticsWithErrorBoundary />
      </WeightProvider>
    </BrowserRouter>
  </React.StrictMode>
);
